import styled from "styled-components";
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import { calculateHeatmap } from "../Heatmap/calculations";
import Filters from "../Filters";
import Empty from '../Empty';
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";
import { validateAndSanitizeOutcomes } from "../utils/outcomesDataIntegrity";
import { usePageTranslations } from 'hooks/usePageTranslations';
import {
  extractEmployeeAuthDetails,
  parseAnalyticsPermissionsFromDetails,
} from "utilities/analyticsPermissions";
import { hasOutcomeResponsesInFlatData } from "../utils/outcomeResponses";
import { isStaplesOrg } from "utilities/organizationUtils";

// Helper function to strip markdown and HTML tags from question text
const stripMarkdownAndHtml = (text) => {
  if (!text || typeof text !== 'string') return text;

  let cleaned = text;

  // Remove HTML tags (including nested tags)
  cleaned = cleaned.replace(/<[^>]*>/g, '');

  // Remove markdown bold syntax (**text** or __text__)
  cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, '$1');
  cleaned = cleaned.replace(/__(.*?)__/g, '$1');

  // Remove markdown italic syntax (*text* or _text_)
  cleaned = cleaned.replace(/\*(.*?)\*/g, '$1');
  cleaned = cleaned.replace(/_(.*?)_/g, '$1');

  // Remove markdown code syntax (`code`)
  cleaned = cleaned.replace(/`([^`]*)`/g, '$1');

  // Remove markdown links [text](url)
  cleaned = cleaned.replace(/\[([^\]]*)\]\([^\)]*\)/g, '$1');

  // Remove markdown images ![alt](url)
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^\)]*\)/g, '$1');

  // Decode common HTML entities
  cleaned = cleaned.replace(/&nbsp;/g, ' ');
  cleaned = cleaned.replace(/&amp;/g, '&');
  cleaned = cleaned.replace(/&lt;/g, '<');
  cleaned = cleaned.replace(/&gt;/g, '>');
  cleaned = cleaned.replace(/&quot;/g, '"');
  cleaned = cleaned.replace(/&#39;/g, "'");

  // Clean up extra whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
};

const QuestionAnalytics = ({ outcomeQ: outcomeQProp, outcomeStandards: outcomeStandardsProp }) => {
  const t = usePageTranslations('app/audit/simple/QuestionAnalytics/translations');
  const navigate = useNavigate();

  // Redirect protection for custom data from NewAnalytics
  const { hasCustomData } = useCustomDataRedirect();

  // Get employee and permission data for access control
  const { employee, permissionRecords, employeeId } = useSelector(
    (state) => ({
      employee: state.employees?.userEmp,
      permissionRecords: state.employee_permission?.employee_permission,
      employeeId: state.auth?.employee_id,
    }),
    shallowEqual
  );

  // CRITICAL: Permission check - redirect users without question-analytics permission
  // This check MUST happen before any rendering to prevent unauthorized access
  // Users CANNOT access this page under ANY circumstances if they do not have the permission
  useEffect(() => {
    const isAdmin = Number(employee?.account_type) === 5;

    // Admins have access to everything - no redirect needed
    if (isAdmin) {
      return;
    }

    // Check base analytics access - required for any analytics feature
    if (employee?.access_data_analytics !== 1) {
      console.warn("User does not have base analytics access, redirecting from question-analytics");
      navigate('/leader/audit/new-analytics', { replace: true });
      return;
    }

    // Check for question-analytics permission in permission records
    const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);
    const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);

    // SECURITY: Require explicit question-analytics permission
    // If granular permissions exist, check for question-analytics
    if (parsedAnalytics) {
      // Granular permissions exist - require explicit question-analytics permission
      if (parsedAnalytics['question-analytics'] !== true) {
        console.warn("User does not have question-analytics permission, redirecting");
        navigate('/leader/audit/new-analytics', { replace: true });
        return;
      }
    } else {
      // No granular permissions found - for security, require explicit permission
      // This ensures users cannot access question-analytics without explicit grant
      console.warn("No granular analytics permissions found, question-analytics requires explicit permission, redirecting");
      navigate('/leader/audit/new-analytics', { replace: true });
      return;
    }
  }, [employee, permissionRecords, employeeId, navigate]);

  // Sanitize outcomeQ data to fix duplicate IDs
  const outcomeQ = useMemo(() => {
    if (!outcomeQProp) return null;
    
    const { sanitized, integrity } = validateAndSanitizeOutcomes(outcomeQProp);
    
    if (integrity.hasDuplicates) {
      console.log('[QuestionAnalytics] Outcome data sanitized:', {
        original: outcomeQProp.questions?.map(q => ({ id: q.id, name: q.name })),
        sanitized: sanitized.questions?.map(q => ({ id: q.id, name: q.name })),
        integrity
      });
    }
    
    return sanitized;
  }, [outcomeQProp]);

  const outcomeStandards = outcomeStandardsProp;
  const [dataFormat, setDataFormat] = useState();
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [sortColumn, setSortColumn] = useState("score"); // "question", "type", "parent", "responses", "score"
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" = low to high, "desc" = high to low
  const [questionsData, setQuestionsData] = useState([]);

  const core_data = useSelector(
    (state) => state.audit?.core_data,
    shallowEqual
  );

  const filtered_data = useSelector(
    (state) => state.audit?.filtered_data,
    shallowEqual
  );

  const responses = useSelector(
    (state) => state.debrief_schedule?.culture_audit_reports,
    shallowEqual
  );

  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );

  const anchor = useSelector((state) => state.audit?.anchor, shallowEqual);
  const getAuth = useSelector((state) => state.auth);
  const getOrganizations = useSelector((state) => state.organizations);

  const [outcomesForDisplay, setOutcomesForDisplay] = useState(null);

  const handleOutcomeSelect = (selectedOutcomesArray) => {
    setSelectedOutcomes(selectedOutcomesArray);
  };

  useEffect(() => {
    if (filtered_data && filtered_data.length > 0) {
      if (Array.isArray(filtered_data[0])) {
        setDataFormat(filtered_data);
      } else {
        setDataFormat([filtered_data]);
      }
    } else if (filtered_data) {
      setDataFormat([filtered_data]);
    } else {
      setDataFormat([]);
    }
  }, [filtered_data]);

  // Extract and aggregate question data
  useEffect(() => {
    if (core_data && dataFormat && anchor) {
      let rule = core_data?.Whitelabel?.rule
        ? parseInt(core_data?.Whitelabel?.rule)
        : 2;

      // Check if we need to combine surveys with different name_uids (same logic as Heatmap)
      const isPrimaryAnchor = core_data?.categories[0]?.categories?.find(
        (cat) => cat.id === anchor
      )?.priority === "primary";

      const hasNonPrimaryFilters = selectedFilters?.some((filter) => {
        if (filter.isSubfactor) return false;
        const category = core_data?.categories[0]?.categories?.find((cat) =>
          cat.options?.some((option) => option.id === filter.id)
        );
        return category && category.priority !== "primary";
      });

      let dataToUse = dataFormat;

      const needsToCombineSurveys = isPrimaryAnchor &&
        hasNonPrimaryFilters &&
        responses?.response?.length > 1 &&
        responses.response[0]?.responses;

      if (needsToCombineSurveys) {
        const allSurveys = responses.response;
        const latestDate = Math.max(...allSurveys.map(survey => new Date(survey.date || survey.scheduled_for).getTime()));

        const latestSurveys = allSurveys.filter(survey => {
          const surveyDate = new Date(survey.date || survey.scheduled_for).getTime();
          return surveyDate === latestDate;
        });

        if (latestSurveys.length > 1) {
          const combinedResponses = latestSurveys.flatMap(survey =>
            survey.responses || []
          ).map(response => response.response).filter(r => r && !Array.isArray(r));

          if (combinedResponses.length > 0) {
            dataToUse = [combinedResponses];
          }
        }
      }

      // Check if we're filtering by factors only
      const hasFactorFilters = selectedFilters?.some(
        (filter) => filter.type_of === "factor"
      );
      const shouldIncludeOutcomes = !hasFactorFilters || selectedOutcomes.length > 0;

      const organizationId =
        getAuth?.organization_id ?? getOrganizations?.organization?.id;
      const staplesOrg = isStaplesOrg(organizationId);

      let filteredOutcomeQ = outcomeQ;
      if (staplesOrg) {
        if (
          shouldIncludeOutcomes &&
          outcomeQ?.questions &&
          selectedOutcomes.length > 0
        ) {
          const selectedOutcomeIds = selectedOutcomes
            .map((o) => (o && o.id != null ? Number(o.id) : Number(o)))
            .filter((id) => !isNaN(id));
          if (selectedOutcomeIds.length > 0) {
            filteredOutcomeQ = {
              ...outcomeQ,
              questions: outcomeQ.questions.filter((q) =>
                selectedOutcomeIds.includes(Number(q.id))
              ),
            };
          }
        }
      } else {
        if (shouldIncludeOutcomes && outcomeQ?.questions) {
          let questions = outcomeQ.questions;
          if (selectedOutcomes.length > 0) {
            const selectedOutcomeIds = selectedOutcomes
              .map((o) => (o && o.id != null ? Number(o.id) : Number(o)))
              .filter((id) => !isNaN(id));
            if (selectedOutcomeIds.length > 0) {
              questions = questions.filter((q) =>
                selectedOutcomeIds.includes(Number(q.id))
              );
            }
          }
          const flatData = Array.isArray(dataToUse?.[0]) ? dataToUse[0] : [];
          if (flatData.length > 0) {
            questions = questions.filter((q) =>
              hasOutcomeResponsesInFlatData(flatData, q.id)
            );
          }
          filteredOutcomeQ = { ...outcomeQ, questions };
        }
      }

      setOutcomesForDisplay(filteredOutcomeQ);

      // Calculate heatmap data - returns array of anchor options
      let calculatedHeatmapData = calculateHeatmap(
        core_data,
        dataToUse,
        anchor,
        shouldIncludeOutcomes ? filteredOutcomeQ : null,
        shouldIncludeOutcomes ? outcomeStandards : null,
        selectedOutcomes,
        selectedFilters?.length === 0 && selectedOutcomes?.length > 0
      ).filter((f) => f.responses > rule);

      // Extract and aggregate questions
      const questionMap = new Map();

      calculatedHeatmapData.forEach((anchorOption) => {
        const responseCount = anchorOption.responses || 0;
        const optionName = anchorOption.option;

        // Extract Category questions
        if (anchorOption.culture_scores?.item_scores) {
          Object.entries(anchorOption.culture_scores.item_scores).forEach(([itemKey, itemData]) => {
            if (!questionMap.has(itemKey)) {
              questionMap.set(itemKey, {
                id: itemKey,
                question: stripMarkdownAndHtml(itemData.question),
                type: t('categoryType'),
                parent: itemData.factor_name,
                totalScore: 0,
                totalResponses: 0,
                optionScores: {}, // Store scores per anchor option
              });
            }
            const question = questionMap.get(itemKey);
            if (itemData.scores !== null && itemData.scores !== undefined && !isNaN(itemData.scores)) {
              question.totalScore += itemData.scores * responseCount;
              question.totalResponses += responseCount;
              question.optionScores[optionName] = itemData.scores;
            }
          });
        }

        // Extract Outcome questions
        if (anchorOption.outcome_scores?.question_scores) {
          Object.entries(anchorOption.outcome_scores.question_scores).forEach(([itemKey, itemData]) => {
            if (!questionMap.has(itemKey)) {
              questionMap.set(itemKey, {
                id: itemKey,
                question: stripMarkdownAndHtml(itemData.question),
                type: t('outcomeType'),
                parent: itemData.outcome,
                totalScore: 0,
                totalResponses: 0,
                optionScores: {}, // Store scores per anchor option
              });
            }
            const question = questionMap.get(itemKey);
            if (itemData.scores !== null && itemData.scores !== undefined && !isNaN(itemData.scores)) {
              question.totalScore += itemData.scores * responseCount;
              question.totalResponses += responseCount;
              question.optionScores[optionName] = itemData.scores;
            }
          });
        }
      });

      // Calculate final aggregated scores (Overall)
      const questions = Array.from(questionMap.values()).map((q) => ({
        ...q,
        score: q.totalResponses > 0 ? q.totalScore / q.totalResponses : null,
      }));

      setQuestionsData(questions);
    }
  }, [
    core_data,
    dataFormat,
    anchor,
    outcomeQ,
    outcomeStandards,
    selectedFilters,
    selectedOutcomes,
    responses,
    getAuth?.organization_id,
    getOrganizations?.organization?.id,
  ]);

  // Sort questions by selected column
  const sortedQuestions = useMemo(() => {
    const sorted = [...questionsData].sort((a, b) => {
      let comparison = 0;
      
      switch (sortColumn) {
        case "question":
          const questionA = (a.question || "").toLowerCase();
          const questionB = (b.question || "").toLowerCase();
          comparison = questionA.localeCompare(questionB);
          break;
        case "type":
          const typeA = (a.type || "").toLowerCase();
          const typeB = (b.type || "").toLowerCase();
          comparison = typeA.localeCompare(typeB);
          break;
        case "parent":
          const parentA = (a.parent || "").toLowerCase();
          const parentB = (b.parent || "").toLowerCase();
          comparison = parentA.localeCompare(parentB);
          break;
        case "responses":
          comparison = (a.totalResponses ?? -1) - (b.totalResponses ?? -1);
          break;
        case "score":
        default:
          const scoreA = a.score ?? -1;
          const scoreB = b.score ?? -1;
          comparison = scoreA - scoreB;
          break;
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [questionsData, sortDirection, sortColumn]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const formatScoreDisplay = (score) => {
    if (
      score === null ||
      score === undefined ||
      isNaN(score) ||
      score === "NaN" ||
      score === ""
    ) {
      return "-";
    }
    return typeof score === "number" ? score.toFixed(1) : score;
  };

  // CRITICAL: Permission check before rendering - prevent any unauthorized access
  // This synchronous check ensures no content is rendered if user lacks permission
  const isAdmin = Number(employee?.account_type) === 5;
  const hasBaseAnalyticsAccess = employee?.access_data_analytics === 1;

  let hasQuestionAnalyticsPermission = false;
  if (isAdmin) {
    hasQuestionAnalyticsPermission = true; // Admins have access
  } else if (hasBaseAnalyticsAccess) {
    const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);
    const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);
    if (parsedAnalytics) {
      hasQuestionAnalyticsPermission = parsedAnalytics['question-analytics'] === true;
    }
    // If no granular permissions exist, deny access for security
  }

  // If user doesn't have permission, return null immediately (redirect will happen in useEffect)
  if (!hasQuestionAnalyticsPermission) {
    return null;
  }

  if (core_data?.noData && !hasCustomData) {
    return <Empty />;
  }


  console.log(outcomeQ)
  return (
    <Container>
      <AnalyticsBreadcrumb currentComponent="question-analytics" />
      <HeaderRow>
        <Title>
          <TitleIconWrapper aria-hidden>
            <QuestionIcon />
          </TitleIconWrapper>
          {t('questionAnalytics')}
        </Title>
      </HeaderRow>
      <Description>
        {t('description')}
      </Description>

      <AnchorFilterRow>
        <Filters
          outcomes={outcomesForDisplay?.questions ?? outcomeQ?.questions}
          onSelectOutcomes={handleOutcomeSelect}
          hideFactors={false}
          filters={true}
        />
      </AnchorFilterRow>

      <Content>
        {sortedQuestions.length === 0 ? (
          <Empty />
        ) : (
          <>
            <Table>
              <thead>
                <TableHeaderRow>
                  <SortableHeader 
                    onClick={() => handleSort("question")}
                    isActive={sortColumn === "question"}
                  >
                    <HeaderContent>
                        {t('question')}
                      {sortColumn === "question" && (
                        <SortIcon>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </SortIcon>
                      )}
                    </HeaderContent>
                  </SortableHeader>
                    {/* <SortableHeader
                    onClick={() => handleSort("type")}
                    isActive={sortColumn === "type"}
                  >
                    <HeaderContent>
                      Question Type
                      {sortColumn === "type" && (
                        <SortIcon>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </SortIcon>
                      )}
                    </HeaderContent>
                  </SortableHeader> */}
                  <SortableHeader 
                    onClick={() => handleSort("parent")}
                    isActive={sortColumn === "parent"}
                  >
                    <HeaderContent>
                        {t('category')}
                      {sortColumn === "parent" && (
                        <SortIcon>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </SortIcon>
                      )}
                    </HeaderContent>
                  </SortableHeader>
                  <SortableHeader 
                      onClick={() => handleSort("responses")}
                      isActive={sortColumn === "responses"}
                    >
                      <HeaderContent>
                        {t('numberOfRespondents')}
                        {sortColumn === "responses" && (
                          <SortIcon>
                            {sortDirection === "asc" ? "↑" : "↓"}
                          </SortIcon>
                        )}
                      </HeaderContent>
                    </SortableHeader>
                    <SortableHeader 
                    onClick={() => handleSort("score")}
                    isActive={sortColumn === "score"}
                  >
                    <HeaderContent>
                        {t('score')}
                      {sortColumn === "score" && (
                        <SortIcon>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </SortIcon>
                      )}
                    </HeaderContent>
                  </SortableHeader>
                </TableHeaderRow>
              </thead>
              <tbody>
                {sortedQuestions.map((question, idx) => (
                  <TableRow key={question.id || idx}>
                    <QuestionCell>{question.question}</QuestionCell>
                    {/* <TypeCell>
                      <Badge type={question.type}>
                        {question.type}
                      </Badge>
                    </TypeCell> */}
                    <ParentCell>
                      {question.parent}
                    </ParentCell>
                    <ResponsesCell>
                      {question.totalResponses ?? 0}
                    </ResponsesCell>
                    <ScoreCell>
                      {formatScoreDisplay(question.score)}
                    </ScoreCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Content>
    </Container>
  );
};

export default QuestionAnalytics;

const QuestionIcon = () => (
  <svg
        width="24"
        height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
            d="M10 8.00224C10.1762 7.50136 10.524 7.07901 10.9817 6.80998C11.4395 6.54095 11.9777 6.4426 12.501 6.53237C13.0243 6.62213 13.499 6.89421 13.8409 7.30041C14.1829 7.70661 14.37 8.22072 14.3692 8.75168C14.3692 10.2506 12.1209 11 12.1209 11M12.1499 14H12.1599M7 18V20.3355C7 20.8684 7 21.1348 7.10923 21.2716C7.20422 21.3906 7.34827 21.4599 7.50054 21.4597C7.67563 21.4595 7.88367 21.2931 8.29976 20.9602L10.6852 19.0518C11.1725 18.662 11.4162 18.4671 11.6875 18.3285C11.9282 18.2055 12.1844 18.1156 12.4492 18.0613C12.7477 18 13.0597 18 13.6837 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V14C3 14.93 3 15.395 3.10222 15.7765C3.37962 16.8117 4.18827 17.6204 5.22354 17.8978C5.60504 18 6.07003 18 7 18Z"
      stroke="currentColor"
            strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: 'Raleway';
  overflow-y: visible;
  position: relative;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 15px;
  color: #2d70e2;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #F8F9FA;
  border-radius: 7px;
  color: #1F2937;
`;

const Description = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 30px;
  line-height: 1.5;
  color: #2A3039;
  max-width: 70%;
`;

const T2 = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  margin-top: 20px;
`;

const AnchorFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const Content = styled.div`
  border-radius: 10px;
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
  margin-bottom: 50px;
  min-height: 250px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
`;

const TableHeaderRow = styled.tr`
  background-color: #F8FAFF;
  border-bottom: 2px solid #ddd;
  height: 40px;
`;

const SortableHeader = styled.th`
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #2A3039;
  text-transform: uppercase;
  border-right: 1px solid #ddd;
  background-color: #F8FAFF;
  cursor: pointer;
  user-select: none;
  position: sticky;
  z-index: 4;
  
  &:hover {
    background-color: #EBF1FD;
  }
  
  /* First column - Question */
  &:first-child {
    left: 0;
    width: 300px;
    min-width: 300px;
    text-align: left;
  }
  
  /* Second column - Category */
  &:nth-child(2) {
    left: 300px;
    width: 160px;
    min-width: 160px;
    text-align: center;
  }
  
  /* Third column - Total Responses */
  &:nth-child(3) {
    left: 460px;
    width: 120px;
    min-width: 120px;
    text-align: center;
  }
  
  /* Fourth column - Score */
  &:nth-child(4) {
    text-align: center;
  }
`;

const HeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SortIcon = styled.span`
  font-size: 12px;
  color: #2D70E2;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #ddd;
  
  &:hover {
    background-color: #F8FAFF;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const QuestionCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #2A3039;
  max-width: 300px;
  width: 300px;
  min-width: 300px;
  word-wrap: break-word;
  border-right: 1px solid #ddd;
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 2;
`;

const TypeCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #2A3039;
  font-weight: 500;
  border-right: 1px solid #ddd;
  white-space: nowrap;
  width: 120px;
  min-width: 120px;
  position: sticky;
  left: 300px;
  background: #fff;
  z-index: 2;
  text-align: center;
  vertical-align: middle;
`;

const ParentCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #2A3039;
  border-right: 1px solid #ddd;
  white-space: nowrap;
  width: 160px;
  min-width: 160px;
  position: sticky;
  left: 300px;
  background: #fff;
  z-index: 2;
  text-align: center;
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: Raleway;
  font-size: 12px;
  font-weight: 600;
  line-height: 140%;
  padding: 6px 12px;
  border-radius: 16px;
  
  ${props => {
    if (props.type === "Category") {
      // Blue badge for Categories (from Filters.js SelectedOption style, but with blue accent)
      return `
        color: #2D70E2;
        background-color: #EBF1FD;
      `;
    } else if (props.type === "Outcome") {
      // Green badge for Outcomes (from ScheduleV2 StatusBadge active style)
      return `
        color: #2e7d2e;
        background-color: #e8f5e8;
      `;
    }
    // Default gray
    return `
      color: #2a3039;
      background-color: #f0f2f5;
    `;
  }}
`;

const ScoreCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  width: 50px;
  min-width: 50px;
  font-weight: 500;
  background-color: #fff;
  color: #2A3039;
  border-right: 1px solid #ddd;
  font-size: 14px;
  white-space: nowrap;
`;

const ResponsesCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  width: 120px;
  min-width: 120px;
  background-color: #fff;
  color: #2A3039;
  border-right: 1px solid #ddd;
  font-size: 14px;
  white-space: nowrap;
  position: sticky;
  left: 460px;
  z-index: 2;
`;
